import { MoreOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { useAppDispatch } from '~/common/stores';
import { breakpoint } from '~/common/utils/breakpoint.utils.ts';
import { APP_FRAME_CSS, FONT_WEIGHT, TOKEN } from '~/configs/theme.ts';
import useMatching from '~/features/matching/hooks/useMatching.ts';
import useProfile from '~/features/profile/hooks/useProfile.ts';
import { fetchMyProfile } from '~/features/profile/stores/profile.slice.ts';

/**
 * 데스크톱 전역 네비게이션 (lg 이상). 하단 탭을 대체한다.
 *
 * @description 하단 탭은 엄지가 닿는 자리라 모바일에서 1급 네비게이션이지만, 데스크톱에서는
 *  시선이 가지 않는 화면 맨 아래다. 넓은 화면에서는 같은 목적지를 상단으로 올린다.
 *
 *  탭 4개(홈·검색·채팅·설정)를 그대로 옮기지 않는다. **설정은 오른쪽 끝으로 뺀다** — 자주 쓰지
 *  않는 항목이 가로 네비게이션의 1급 자리를 차지하면, 정작 역할별 콘솔 진입구가 갈 곳이 없다.
 *  콘솔 링크는 지금 설정 화면 안에 묻혀 있어 지도사가 매번 두 단계를 거쳐야 한다.
 */
const LINKS = [
  { to: '/', label: '홈', end: true },
  { to: '/search', label: '클래스 찾기', end: false },
  { to: '/chat', label: '채팅', end: false },
];

interface GlobalNavProps {
  /** 본문이 프레임 폭을 벗어나는 화면(콘솔)에서 참 — 헤더도 같이 벗어나야 좌우 선이 맞는다. */
  fluid?: boolean;
}

function GlobalNav({ fluid = false }: GlobalNavProps) {
  const dispatch = useAppDispatch();
  // 안읽음 총합은 네비게이션에서만 쓰는 파생값이라 구독만 한다(fetch 는 각 페이지 책임 — BottomTab 과 같다).
  const { totalUnread } = useMatching();
  const { me, isMeLoaded } = useProfile();

  /*
    역할은 이 컴포넌트가 직접 불러온다 — 전역 네비게이션은 모든 화면에 떠 있는데, 프로필을 fetch 하는
    페이지(홈·설정·채팅방 등)는 일부뿐이다. 페이지에 맡기면 클래스 상세로 직접 들어왔을 때 콘솔 링크가
    통째로 사라진다(실제로 그랬다 — 홈에서는 보이고 상세에서는 안 보였다).
    `isMeLoaded` 로 막아 화면을 옮길 때마다 다시 부르지 않는다. RoleGuard 와 같은 방식이다.
    비로그인이면 API 가 null 을 돌려주므로 로그인 여부와 무관하게 한 번만 돈다.
  */
  useEffect(() => {
    if (!isMeLoaded) dispatch(fetchMyProfile());
  }, [dispatch, isMeLoaded]);

  const consoleLink =
    me?.role === 'admin'
      ? { to: '/admin', label: '운영 콘솔' }
      : me?.role === 'tutor'
        ? { to: '/tutor-console', label: '지도사 콘솔' }
        : null;

  return (
    <header css={styles.bar}>
      <div css={[styles.inner, fluid && styles.innerFluid]}>
        <Link css={styles.brand} to="/">
          Doddies
        </Link>

        <nav css={styles.nav}>
          {LINKS.map(link => (
            <NavLink
              key={link.to}
              className={({ isActive }) => (isActive ? 'active' : '')}
              css={styles.link}
              end={link.end}
              to={link.to}
            >
              {link.label}
              {link.to === '/chat' && totalUnread > 0 && (
                <span css={styles.badge}>{totalUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div css={styles.right}>
          {consoleLink && (
            <NavLink css={styles.console} to={consoleLink.to}>
              {consoleLink.label}
            </NavLink>
          )}
          <NavLink aria-label="설정" css={styles.settings} to="/settings">
            <MoreOutlined />
          </NavLink>
        </div>
      </div>
    </header>
  );
}

const styles = {
  bar: css`
    flex: none;
    border-bottom: 1px solid ${TOKEN.border};
    background: ${TOKEN.bgSurface};
  `,
  /*
    본문과 같은 폭·같은 좌우 여백에 맞춘다 — 헤더의 브랜드와 본문 첫 요소의 왼쪽 선이 어긋나면
    화면이 두 겹으로 보인다. 프레임 폭이 바뀌면(2단계) 여기도 자동으로 따라온다.
  */
  inner: css`
    display: flex;
    align-items: center;
    gap: 28px;
    width: 100%;
    ${APP_FRAME_CSS}
    height: 56px;
    margin: 0 auto;
    padding: 0 20px;
  `,
  /* AppLayout.frameFluid 와 같은 이유로 미디어쿼리 안에서 덮는다(평범한 선언은 밀려 무시된다). */
  innerFluid: css`
    ${breakpoint.lg} {
      max-width: none;
      /* 사이드바(216px) 안쪽 여백과 브랜드의 왼쪽 선을 맞춘다. */
      padding: 0 24px;
    }
  `,
  brand: css`
    flex: none;
    color: ${TOKEN.primary};
    font-size: 18px;
    font-weight: ${FONT_WEIGHT.EXTRA_BOLD};
    letter-spacing: -0.01em;
    text-decoration: none;
  `,
  nav: css`
    display: flex;
    align-items: center;
    gap: 22px;
  `,
  link: css`
    position: relative;
    padding: 18px 0;
    color: ${TOKEN.textSecondary};
    font-size: 14px;
    text-decoration: none;
    transition: color 0.15s;

    &:hover {
      color: ${TOKEN.textPrimary};
    }

    /* 현재 위치는 색만으로 알리지 않는다 — 밑줄을 함께 둬야 색 구분이 어려운 사용자도 읽는다. */
    &.active {
      color: ${TOKEN.textPrimary};
      font-weight: ${FONT_WEIGHT.BOLD};
      box-shadow: inset 0 -2px 0 ${TOKEN.primary};
    }
  `,
  badge: css`
    display: inline-grid;
    place-items: center;
    min-width: 16px;
    height: 16px;
    margin-left: 5px;
    padding: 0 4px;
    border-radius: 8px;
    background: ${TOKEN.primary};
    color: #fff;
    font-size: 10px;
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
    line-height: 1;
    vertical-align: 1px;
  `,
  right: css`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
  `,
  console: css`
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 14px;
    border: 1px solid ${TOKEN.primary};
    border-radius: 16px;
    color: ${TOKEN.primary};
    font-size: 13px;
    font-weight: ${FONT_WEIGHT.BOLD};
    text-decoration: none;
  `,
  settings: css`
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 16px;
    color: ${TOKEN.textSecondary};
    font-size: 18px;
    text-decoration: none;

    &:hover {
      background: ${TOKEN.bgMuted};
      color: ${TOKEN.textPrimary};
    }
  `,
};

export default GlobalNav;
